import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { logger } from '@deskbay/shared';
import { useAuth } from './AuthContext';
import { useRestaurantStore } from '../store/restaurantStore';
import {
    getSystemNotifications,
    markAsRead,
    markAllAsRead,
    markManyAsReadAndDismissed,
    dismissNotification,
    type SystemNotification
} from '../services/notificationService';
import { configurationAgent, type ConfigAlert } from '../services/configurationAgent';
import { oneSignalService } from '../services/oneSignalService';
import { supabase } from '@deskbay/shared/services/supabase/client';
import { storage } from '../services/storage';
import { useAppFocus } from '../hooks/useAppFocus';

interface NotificationContextType {
    notifications: SystemNotification[];
    unreadCount: number;
    loading: boolean;
    refresh: () => Promise<void>;
    markOneAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    markManyAsReadAndDismissed: (ids: string[]) => Promise<void>;
    dismiss: (id: string) => Promise<void>;
    // New: Config Alerts
    configAlerts: ConfigAlert[];
    dismissConfigAlert: (id: string) => void;
    restoreConfigAlert: (id: string) => void;
    // Expose dismissed state
    isConfigAlertDismissed: (id: string) => boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { session } = useAuth();
    const data = useRestaurantStore((state) => state.data);
    const [notifications, setNotifications] = useState<SystemNotification[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const restaurantId = data?.directoryId ? String(data.directoryId) : undefined;
    const userId = session?.user?.id;

    // Fetch notifications
    const refresh = useCallback(async () => {
        if (!userId) {
            setNotifications([]);
            return;
        }

        if (!restaurantId) {
            setNotifications([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);

            const fetchedData = await getSystemNotifications(restaurantId);
            setNotifications(fetchedData);
        } catch (error) {
            logger.notifications.error('Error fetching notifications', error, 'NotificationContext');
        } finally {
            setLoading(false);
        }
    }, [restaurantId, userId]);

    // Ref al canale attivo — necessario per poterlo rimuovere esplicitamente al foreground
    const channelRef = useRef<RealtimeChannel | null>(null);

    // Estrae la creazione del canale in una callback riutilizzabile,
    // così può essere chiamata sia all'avvio che al ritorno in foreground.
    const setupChannel = useCallback(() => {
        // Rimuovi eventuale canale precedente (es. caduto dopo background)
        if (channelRef.current) {
            supabase.removeChannel(channelRef.current);
            channelRef.current = null;
        }

        logger.notifications.info('Connecting to Supabase Realtime', 'NotificationContext');

        const channel = supabase.channel('notification-updates')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'system_notifications',
                },
                (payload) => {
                    logger.notifications.info('Realtime update received (system_notifications)', 'NotificationContext', { eventType: payload.eventType });
                    refresh();
                }
            )
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'notification_user_states',
                    filter: userId ? `user_id=eq.${userId}` : undefined,
                },
                (payload) => {
                    logger.notifications.info('Realtime update received (user_state)', 'NotificationContext', { eventType: payload.eventType });
                    refresh();
                }
            )
            .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    logger.notifications.info('Realtime Connected', 'NotificationContext');
                }
            });

        channelRef.current = channel;
    }, [refresh, userId]);

    // Initial load and realtime subscription
    useEffect(() => {
        refresh();
        oneSignalService.init();
        setupChannel();

        return () => {
            logger.notifications.info('Disconnecting Realtime', 'NotificationContext');
            if (channelRef.current) {
                supabase.removeChannel(channelRef.current);
                channelRef.current = null;
            }
        };
    }, [refresh, userId, setupChannel]);

    // ─── FOREGROUND RECOVERY — NOTIFICATIONS ─────────────────────────────────
    // Al ritorno in foreground (mobile background o cambio tab web), ricrea il canale
    // (potrebbe essere caduto) e forza un refresh immediato dei dati.
    const handleNotificationFocus = useCallback(async () => {
        logger.notifications.info('App focus: reconnecting notifications channel', 'NotificationContext');
        setupChannel();
        await refresh();
    }, [setupChannel, refresh]);

    useAppFocus(handleNotificationFocus);

    // OneSignal Logout Sync — il login è gestito da AuthContext (SIGNED_IN event).
    // Il logout viene fatto qui perché NotificationContext ha accesso diretto a userId.
    useEffect(() => {
        if (!userId) {
            oneSignalService.logout();
        }
    }, [userId]);

    // Computed unread count (not dismissed and not read)
    // Note: If you want badge to show only unread, or unread + undismissed?
    // Based on previous logic: activeSystemCount = systemNotifications.filter(n => !n.is_dismissed).length
    // And getUnreadCount in service does !read && !dismissed.
    // Usually 'unread' implies the badge count.
    // Let's stick to the previous logic: Count active (undismissed) notifications, 
    // or specifically unread ones?
    // The previous dashboard code was: system: systemNotifications.filter(n => !n.is_dismissed).length
    // This counts "Inbox" items. Even if read, if not dismissed, they are in the list.
    // Let's expose both or just 'activeCount' for the badge.
    // Actually, usually badge = unread. But previously it was "active".
    // I'll stick to 'unread' for property name, but logic will match "active" (undismissed) if that's what was used,
    // OR strict unread. 
    // Let's use !is_read && !is_dismissed for "new" notifications.
    // AND !is_dismissed for "all visible" notifications.

    // Previous code:
    // system: systemNotifications.filter(n => !n.is_dismissed).length
    // So it was counting ALL undismissed notifications.
    // Previous code:
    // system: systemNotifications.filter(n => !n.is_dismissed).length
    // So it was counting ALL undismissed notifications.

    const handleMarkOneAsRead = useCallback(async (id: string) => {
        // Optimistic update
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        await markAsRead(id);
        // Refresh to sync with database
        await refresh();
    }, [refresh]);

    const handleMarkAllAsRead = useCallback(async () => {
        if (!restaurantId) {
            logger.notifications.error('restaurantId is undefined/null, aborting', undefined, 'NotificationContext');
            return;
        }

        // Optimistic update
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));

        try {
            await markAllAsRead(restaurantId);
        } catch (error) {
            logger.notifications.error('markAllAsRead failed', error, 'NotificationContext');
        }

        // Refresh to sync with database
        await refresh();
    }, [refresh, restaurantId]);

    const handleMarkManyAsReadAndDismissed = useCallback(async (ids: string[]) => {
        if (ids.length === 0) return;

        // Optimistic update
        setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, is_read: true, is_dismissed: true } : n));

        try {
            await markManyAsReadAndDismissed(ids);
        } catch (error) {
            logger.notifications.error('markManyAsReadAndDismissed failed', error, 'NotificationContext');
        }

        // Refresh to sync with database
        await refresh();
    }, [refresh]);

    const handleDismiss = useCallback(async (id: string) => {
        // Optimistic update
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_dismissed: true } : n));
        await dismissNotification(id);
        // Refresh to sync with database
        await refresh();
    }, [refresh]);

    // --- CONFIG ALERTS LOGIC ---
    const [dismissedConfigIds, setDismissedConfigIds] = useState<Set<string>>(new Set());

    // Load dismissed config alerts from storage on mount
    useEffect(() => {
        const loadDismissedAlerts = async () => {
            try {
                const stored = await storage.get('dismissedConfigAlerts');
                if (stored) {
                    setDismissedConfigIds(new Set(JSON.parse(stored)));
                }
            } catch {
                // Ignore errors
            }
        };
        loadDismissedAlerts();
    }, []);

    // Helper to map category to permission


    const configAlerts = React.useMemo(() => {
        if (!data) return [];
        // Return all alerts as requested, skipping permission checks
        return configurationAgent.analyzeConfiguration(data);
    }, [data]);

    const dismissConfigAlert = (id: string) => {
        setDismissedConfigIds(prev => {
            const newSet = new Set(prev);
            newSet.add(id);
            storage.set('dismissedConfigAlerts', JSON.stringify([...newSet]));
            return newSet;
        });
    };

    const restoreConfigAlert = (id: string) => {
        setDismissedConfigIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            storage.set('dismissedConfigAlerts', JSON.stringify([...newSet]));
            return newSet;
        });
    };

    // --- TOTAL UNREAD MARKER ---
    // Badge = notifiche attive (non archiviate)
    // System: !is_dismissed
    // Config: !dismissedConfigIds.has(id)
    const systemUnread = notifications.filter(n => !n.is_dismissed && !n.is_read).length;
    const configUnread = configAlerts.filter(a => !dismissedConfigIds.has(a.id)).length;
    const unreadCount = systemUnread + configUnread;

    const isConfigAlertDismissed = (id: string) => dismissedConfigIds.has(id);

    return (
        <NotificationContext.Provider value={{
            notifications,
            configAlerts, // Expose raw valid alerts
            unreadCount,
            loading,
            refresh,
            markOneAsRead: handleMarkOneAsRead,
            markAllAsRead: handleMarkAllAsRead,
            markManyAsReadAndDismissed: handleMarkManyAsReadAndDismissed,
            dismiss: handleDismiss,
            dismissConfigAlert,
            restoreConfigAlert,
            isConfigAlertDismissed
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};
